/**
 * ==============================================
 * MONTHS SUBSCRIPTION PICKER - WEB COMPONENT
 * ==============================================
 *
 * Custom HTML element <subscription-picker> that manages Bob's subscription product selection flow.
 *
 * CORE RESPONSIBILITIES:
 * - Manage refill frequency selection (Monthly/Quarterly/One-time)
 * - Handle scent pack selection with quantity controls
 * - Calculate dynamic pricing based on customer type and selections
 * - Update UI elements (prices, images, feature lists)
 * - Validate selections before checkout
 * - Dispatch custom events for cart integration
 *
 * STATE MANAGEMENT:
 * - selectedRefillFrequency: Current frequency choice (monthly/quarterly/oneoff)
 * - selectedScents: Array of selected scent packs with quantities
 * - selectedAddons: Array of additional products (for one-time purchases)
 * - addonsTotalPrice: Total cost of add-ons in pence
 *
 * PRICING STRUCTURE:
 * - First-time customers: Get shower pack + refills at discounted rate
 * - Refill customers: Only pay for refills (no shower pack)
 * - Pricing differs by frequency and customer type
 *
 * INTEGRATION:
 * - Reads config data from JSON script tag (#months-product-subscription-picker-data)
 * - Integrates with Recharge for subscription management
 * - Communicates with mps--add-to-cart.js via custom events
 */
class MonthsSubscriptionPicker extends HTMLElement {
  constructor() {
    super();

    this.element = this;

    /**
     * Maps internal frequency values to display text
     * Used throughout the component for consistency
     */
    this.REFILL_FREQUENCY_MAP = {
      monthly: 'Every Month',
      quarterly: 'Every 3 Months', // Displayed as "Discovery Plan" in UI but maps to 'Every 3 Months' for pricing lookup
      trial: '14 Day Trial', // 14-day trial, transitions to quarterly via Recharge
      oneoff: 'One off Purchase',
      gift: 'Gift Purchase',
    };

    /**
     * Price per additional scent pack for one-time purchases (in pence)
     * £8.00 = 800 pence
     */
    this.SCENT_ADDON_PRICE_PENCE = 800;

    /**
     * Customer type determines which pricing tier to use
     * Options: 'FIRST TIME ORDER' or 'Recurring Order'
     * Currently hardcoded to FIRST TIME ORDER
     * TODO: Could be made dynamic based on customer history
     */
    this.CUSTOMER_TYPE = 'FIRST TIME ORDER';

    // Component state — default frequency set after configData is loaded below
    this.selectedRefillFrequency = 'quarterly';
    this.refillProductOnly = this.element.dataset.refillProductOnly === 'true'; // Customer already has shower pack?
    this.addonsTotalPrice = 0; // Total cost of selected add-ons (pence)
    this.addonsBox = this.element.querySelector('.one-time-addons-box'); // Add-ons container element
    this.giftNotificationWidget = this.element.querySelector('gift-notification-widget'); // Gift notification widget element
    this.giftRecipientCount = 1; // Number of gift recipients (1-5)
    this.selectedScents = []; // Array of selected scent objects with quantities
    this.selectedScentSkusOrdered = []; // Ordered array of selected scent SKUs, e.g. ['x', 'x', 'y']
    this.selectedAddons = []; // Array of selected add-on products
    this.selectedColor = null; // Selected applicator color

    // Bind event handler for add-on changes (used with addEventListener)
    this.handleAddonChangeBinding = this.handleAddonChange.bind(this);

    /**
     * Load configuration data from JSON script tag
     * Contains product variants, images, and copy for different combinations
     */
    try {
      this.configData = JSON.parse(document.getElementById('months-product-subscription-picker-data').textContent);
      // Default frequency: whichever option appears first in the UI
      if (this.configData.show_trial_plan && this.configData.trial_plan_position === 'first') {
        this.selectedRefillFrequency = 'trial';
      } else if (this.configData.show_discovery_plan !== false) {
        this.selectedRefillFrequency = 'quarterly';
      } else if (this.configData.show_trial_plan) {
        this.selectedRefillFrequency = 'trial';
      } else {
        this.selectedRefillFrequency = 'monthly';
      }
      // Initialize selected color with first color option if available (not for trial)
      if (this.configData.color_option_values && this.configData.color_option_values.length > 0 && this.selectedRefillFrequency !== 'trial') {
        this.selectedColor = this.configData.color_option_values[0];
      }
    } catch (error) {
      console.error('Error parsing products data:', error);
    }

    /**
     * One-time purchase settings
     * Can be overridden via window.oneTimeOffSettings if needed
     * This allows external scripts to customize one-time purchase behavior.
     *
     * PRICING LOGIC:
     * - refillProductOnly = true: Customer already has applicator, so they only pay for refills
     *   Price: £8 (800 pence) for 1 refill pack
     * - refillProductOnly = false: First-time customer gets full shower pack
     *   Price: £31.99 (3199 pence) for applicator + mount + 1 refill pack
     *
     * Note: One-time purchases have no discount (price_cents === price_cents_original)
     * so the strikethrough pricing is not shown in the UI for this option.
     */
    const one_time_refill_price = this.configData.one_time_refill_price || 800; // Default to £8.00 if not set in config
    let oneTimeOffSettings = {
      frequency: 'One off Purchase',
      price_cents: this.refillProductOnly ? one_time_refill_price : 3199, // £8 (refill only) or £31.99 (full pack)
      price_cents_original: this.refillProductOnly ? one_time_refill_price : 3199, // No discount for one-time
      whats_included_in_order: 'Shower pack',
      per_refill_pack_cost_cents: one_time_refill_price, // £8 per additional refill pack add-on
      barsQty: 1, // Base quantity: 1 pack = 2 bars
    };

    window.oneTimeOffSettings = window.oneTimeOffSettings || {};
    oneTimeOffSettings = { ...oneTimeOffSettings, ...window.oneTimeOffSettings };

    // Parse trial plan price from settings (e.g. "£11" or "£9.99") to pence, default 1100
    const trialPriceRaw = this.configData?.trial_plan_price || '£11';
    const trialPricePence = Math.round(parseFloat(trialPriceRaw.replace(/[^0-9.]/g, '')) * 100) || 1100;

    /**
     * ==============================================
     * PRICING MAP - CORE PRICING LOGIC
     * ==============================================
     * Defines all pricing tiers based on customer type and refill frequency.
     *
     * STRUCTURE:
     * {
     *   money_format: Shopify money format string
     *   'CUSTOMER_TYPE': [
     *     {
     *       frequency: Display name
     *       price_cents: First order price (discounted)
     *       price_cents_original: Original price (for strikethrough)
     *       per_refill_pack_cost_cents: Cost per individual refill pack
     *       refill: {
     *         price_cents: Price per refill delivery
     *         quantity: Number of packs per delivery
     *         frequency: How often
     *       }
     *     }
     *   ]
     * }
     *
     * PRICING BREAKDOWN:
     * - FIRST TIME ORDER prices include shower pack discount
     * - refillProductOnly flag adjusts prices (removes shower pack cost)
     * - Monthly: 1 pack per month
     * - Quarterly: 3 packs per 3 months (bulk discount)
     */
    /**
     * PRICING MAP - Master pricing configuration
     *
     * All prices are in PENCE (e.g., 700 = £7.00)
     * Formatted using Shopify.formatMoney() with money_format template
     */
    this.prices_map = {
      money_format: `£{{amount}}`, // Shopify money format template

      /**
       * FIRST TIME ORDER PRICING
       * These are customers who don't yet have the Bob applicator.
       * They receive the shower pack (applicator + mount) either FREE or at a discount.
       */
      'FIRST TIME ORDER': [
        /**
         * MONTHLY SUBSCRIPTION
         * Best value for first-time customers - FREE applicator
         * First order: £15 (discounted from £31.99) OR £12 for refill-only customers
         * Ongoing: £12/month for 2 refill packs (4 bars)
         */
        {
          frequency: 'Every Month',
          price_cents: this.refillProductOnly ? 1200 : 1500, // £12 refill-only (2 packs × £6) OR £15 first-time
          price_cents_original: this.refillProductOnly ? 1998 : 3199, // £19.98 refill-only OR £31.99 (for strikethrough)
          whats_included_in_order: 'Shower pack',
          per_refill_pack_cost_cents: 700, // £7 per pack for addon calculations
          refill: {
            price_cents: 600, // £6 per refill pack
            price_cents_original: 800, // £8 original per pack (for savings display)
            quantity: 2, // 2 packs per delivery (4 bars)
            frequency: 'Every Month',
          },
        },
        /**
         * QUARTERLY SUBSCRIPTION — "DISCOVERY PLAN"
         * Best value option — includes 4 refill packs (8 bars)
         * All available scents auto-selected (1 of each)
         * First order: £24 (discounted from £61.96) for first-time, £18 for refill-only
         * Ongoing: £24/3 months (4 packs × £6 each)
         */
        {
          frequency: 'Every 3 Months',
          price_cents: this.refillProductOnly ? 2400 : 2400,
          price_cents_original: this.refillProductOnly ? 3996 : 6196,
          whats_included_in_order: 'Shower pack + 4 x refill packs',
          per_refill_pack_cost_cents: 600, // £6 per pack (bulk discount)
          refill: {
            price_cents: 600, // £6 per pack in ongoing refills
            price_cents_original: 800, // £8 original per pack
            quantity: 4, // 4 packs per delivery (8 bars total)
            frequency: 'Every 3 Months',
          },
        },
        /**
         * 14 DAY TRIAL PLAN
         * Trial period: £13.99 first order (Applicator + Shower Mount + 2 bars)
         * After 14 days: transitions to quarterly subscription (£24/3 months for 8 bars)
         * Customer selects 1 refill pack only
         * Uses product "14-days-trial-1" with its own Recharge selling plan
         */
        {
          frequency: '14 Day Trial',
          price_cents: trialPricePence, // from settings (trial_plan_price)
          price_cents_original: 3199, // £31.99
          whats_included_in_order: 'Shower pack + 1 refill pack',
          per_refill_pack_cost_cents: 600,
          refill: {
            price_cents: 2400,
            price_cents_original: 800,
            quantity: 1, // 1 scent pack for trial
            frequency: '14 Day Trial',
          },
        },
        /**
         * ONE-TIME PURCHASE
         * Imported from oneTimeOffSettings defined above
         * No subscription, no recurring charges
         */
        oneTimeOffSettings,
        /**
         * GIFT PURCHASE
         * One-time purchase intended as a gift
         * Fixed reference price £31.99 - includes shower pack + 1 refill
         * No discount shown (price_cents === price_cents_original)
         * Supports gift recipient notification feature
         */
        {
          frequency: 'Gift Purchase',
          price_cents: 2400, // £24 flat rate
          price_cents_original: 3199, // No discount (no strikethrough)
          whats_included_in_order: 'Shower pack + 1 refill pack',
          per_refill_pack_cost_cents: 800, // £8 if adding extra refills
          barsQty: 1, // 1 pack = 2 bars included
        },
      ],

      /**
       * RECURRING ORDER PRICING
       * For customers already in a subscription who are modifying their plan.
       * NOTE: This section appears unused in current implementation
       * (CUSTOMER_TYPE is always 'FIRST TIME ORDER')
       */
      'Recurring Order': [
        {
          frequency: 'Every Month',
          price_cents: 700, // £7 per refill pack
          whats_included_in_order: '1 x refill pack',
          per_refill_pack_cost_cents: 700,
        },
        {
          frequency: 'Every 3 Months',
          price_cents: 1800, // £18 for 3 packs (£6 each)
          whats_included_in_order: '3 x refill packs',
          per_refill_pack_cost_cents: 600,
        },
      ],
    };

    this._initialized = false;

    // Initialize component
    this.initializeScentSelector();
    this.init();

    this._initialized = true;
  }

  /**
   * Initialize component after construction
   * Sets up event listeners and triggers initial state update
   */
  init() {
    this.bindEvents();
    this.setupGiftRecipientsListener();
    this.updateState();
    // Trigger change event on pre-selected frequency to update UI
    if (this.querySelector('[name="refill_frequency"]:checked')) {
      this.querySelector('[name="refill_frequency"]:checked').dispatchEvent(new Event('change', { bubbles: true }));
    }

    //this.preselectOrdinaryRefillOption();
    this.preselectEveningManRefillOptionForGift();
    this.updateTrialRenewalDate();
    this.autoAdvanceToStepTwoOnLoad();

    // For trial plan, deselect the first color option so user must pick one
    /* if (this.selectedRefillFrequency === 'trial') {
      this.deselectAllColorOptions();
    } */
  }

  autoAdvanceToStepTwoOnLoad() {
    if (this.element.dataset.autoAdvanceToStepTwoOnLoad !== 'true') return;
    if (this.element.dataset.closed !== 'true') return;

    window.requestAnimationFrame(() => {
      if (this.element.dataset.closed === 'true') {
        this.handleAddToCart();
      }
    });
  }

  /**
   * Pre-select "The Ordinary Man" refill option automatically
   *
   * CURRENTLY DISABLED: Called from init() but commented out there.
   * When enabled, this would:
   * 1. Auto-click the "Ordinary Man" scent add button multiple times with delays
   * 2. Listen for frequency changes and auto-add Ordinary Man on each change
   *
   * PRODUCT ID: 15172234543490 = "The Ordinary Man" scent product
   *
   * PURPOSE:
   * This was likely used for testing or a promotion where Ordinary Man
   * was the default/featured scent selection. Clicking multiple times
   * fills the required quantity for different frequencies (monthly=1, quarterly=3).
   *
   * WHY DISABLED:
   * Pre-selecting scents may confuse customers who want to choose their own.
   * Could be re-enabled for promotional campaigns featuring a specific scent.
   */
  preselectOrdinaryRefillOption() {
    const ordinaryManAdd = document.querySelector(`[data-product-id="15172234543490"] [data-action="add"]`);
    if (ordinaryManAdd) {
      ordinaryManAdd.click();
      setTimeout(() => {
        ordinaryManAdd.click();
      }, 500);
      setTimeout(() => {
        ordinaryManAdd.click();
      }, 1500);
    }
    window.addEventListener('subscription-picker:refillFrequencyChanged', (event) => {
      const ordinaryManAdd = document.querySelector(`[data-product-id="15172234543490"] [data-action="add"]`);
      if (ordinaryManAdd) ordinaryManAdd.click();
    });
  }

  preselectEveningManRefillOptionForGift() {
    window.addEventListener('subscription-picker:refillFrequencyChanged', (event) => {
      const ordinaryManAdd = document.querySelector(`[data-product-id="15108941611394"] [data-action="add"]`);
      if (ordinaryManAdd && event.detail.refillFrequency === 'gift') ordinaryManAdd.click();
    });
  }

  /**
   * Set up listener for gift recipient count changes
   */
  setupGiftRecipientsListener() {
    if (this.giftNotificationWidget) {
      this.giftNotificationWidget.addEventListener('gift-recipients-changed', (e) => {
        this.handleGiftRecipientsChanged(e.detail.recipientCount);
      });
    }
  }

  /**
   * Handle gift recipient count change
   * @param {number} recipientCount - New recipient count
   */
  handleGiftRecipientsChanged(recipientCount) {
    this.giftRecipientCount = recipientCount;
    this.updatePricingSummary();
  }

  /**
   * Bind all event listeners for user interactions
   * - Refill frequency radio buttons
   * - Scent card add/remove buttons
   * - Add to cart button
   */
  bindEvents() {
    //console.log('bindEvents');
    // Color selection event listeners
    const colorInputs = this.element.querySelectorAll('.color-option__input');
    colorInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handleColorSelection(e, e.target.closest('.color-option'));
        }
      });
    });

    // Refill frequency inputs - exclude gift supply selector
    const refillInputs = this.element.querySelectorAll('.refill-section .refill-option input[type="radio"]');
    refillInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handleRefillSelection(e, e.target.closest('.refill-option'));
        }
      });
    });

    const scentCards = this.element.querySelectorAll('.scent-card');
    scentCards.forEach((card) => {
      const addBtns = card.querySelectorAll('[data-action="add"]');
      const removeBtn = card.querySelector('[data-action="remove"]');

      addBtns.forEach((addBtn) => {
        if (addBtn) {
          addBtn.addEventListener('click', (e) => {
            this.handleScentAdd(e, card);
          });
        }
      });

      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          this.handleScentRemove(e, card);
        });
      }

      // Card click-to-select for trial plan (single scent, no ADD buttons)
      card.addEventListener('click', (e) => {
        if (this.selectedRefillFrequency !== 'trial') return;
        if (e.target.closest('[data-action]')) return; // ignore button clicks
        const qty = this.getCardQuantity(card);
        if (qty > 0) {
          this.handleScentRemove(e, card);
        } else {
          // Deselect any currently selected scent first, then select this one
          const currentlySelected = this.element.querySelector('.scent-card--selected');
          if (currentlySelected && currentlySelected !== card) {
            this.handleScentRemove(e, currentlySelected);
          }
          this.handleScentAdd(e, card);
        }
      });
    });

    const addToCartBtn = this.querySelector('[data-add-to-cart-btn]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => {
        this.handleAddToCart({ scroll: true, isTrusted: e.isTrusted });
      });
    }

    const debouncedUpdateMainImage = theme.utils.debounce(this.updateMainImage.bind(this), 100);
    window.addEventListener('subscription-picker:colorChanged', debouncedUpdateMainImage);
    window.addEventListener('subscription-picker:scentCardUpdated', debouncedUpdateMainImage);

    // No Subscription toggle link
    const noSubToggle = this.element.querySelector('[data-no-subscription-toggle]');
    if (noSubToggle) {
      noSubToggle.addEventListener('click', () => {
        const oneoffWrapper = this.element.querySelector('[data-oneoff-wrapper]');
        if (oneoffWrapper) {
          oneoffWrapper.style.display = '';
          noSubToggle.style.display = 'none';
          const oneoffInput = oneoffWrapper.querySelector('input[type="radio"]');
          if (oneoffInput) {
            oneoffInput.checked = true;
            oneoffInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }
  }

  /**
   * Update main product image based on selected color
   * Searches for product thumbnails by alt text matching the color
   * Falls back to media-id based selection if alt-based not found
   *
   * @param {CustomEvent} event - colorChanged event with selectedColor and selectedFeaturedMedia
   */
  updateMainImage(event) {
    // Trial with no scent selected: update image based on selected color
    if (this.selectedRefillFrequency === 'trial' && !this.selectedScentSku) {
      const mediaId = event?.detail?.selectedFeaturedMedia || this.selectedFeaturedMedia;
      if (mediaId) {
        const mediaGallery = document.querySelector('media-gallery.product__gallery');
        const mediaCell = document.querySelector(`.product__media[data-media-id="${mediaId}"]`);
        if (mediaGallery && mediaCell) {
          mediaGallery.setActiveMedia(mediaId, mediaCell.classList.contains('product__media--custom-selection'));
        }
      }
      return;
    }

    /*
      we have 3 possible formats for images ALT
      - SKU + SKU + SKU + ...
      - full_pack_<color>_<scentSku> || refill_only
      - color variant media id

      Blow we try to map image alt to media cell and then get media id from it
    */
    const mediaId = event?.detail?.selectedFeaturedMedia || this.selectedFeaturedMedia;
    const color = (this.selectedColor || 'black').toLowerCase();
    const colorSku = event?.detail?.selectedColorSku || this.selectedColorSku;
    const scentSku = event?.detail?.scentSku || this.selectedScentSku || '';
    const isFullPack = !!this.querySelector('.color-picker-section');

    // Build alt handle: "full_pack_<color>_<scentSku>" or "refill_only"
    const altHandle = isFullPack ? `full_pack_${color}_${scentSku.replaceAll('Refill Pack - ', '').replaceAll(' ', '-').toLowerCase()}` : 'refill_only';
    const multiScentAltHandle = colorSku + ' + ' + this.selectedScentSkusOrdered.join(' + ');

    const multiScentAltHandleImage = document.querySelector(`.product__media [alt="${multiScentAltHandle}"]`);
    const colorBasedApplicator = document.querySelector(`.product__media[data-media-id="${mediaId}"]`);

    // Try to find thumbnail by alt text (case-insensitive), fallback to media-id
    const mainImage = multiScentAltHandleImage || document.querySelector(`.product__media [alt="${altHandle}"]`);
    const mediaCell = mainImage?.closest('.product__media') || colorBasedApplicator;
    const mediaGallery = document.querySelector('media-gallery.product__gallery');
    const mediaId_ = mediaCell?.dataset.mediaId || mediaId;

    if (mediaId_) {
      mediaGallery.setActiveMedia(mediaId_, mediaCell.classList.contains('product__media--custom-selection'));
      //slider.select(thumbnailIndex + 1, true);
    }
  }

  handleColorSelection(_event, option, force = false) {
    const color = option.dataset.color;
    if (color === this.selectedColor && !force) return;

    this.selectedColor = color;
    this.selectedColorSku = option.dataset.sku;
    this.selectedFeaturedMedia = option.dataset.featuredMedia;

    // Update UI - remove selected class from all color options
    const allColorOptions = this.element.querySelectorAll('.color-option');
    allColorOptions.forEach((opt) => opt.classList.remove('color-option--selected'));
    option.classList.add('color-option--selected');

    const radioInput = option.querySelector('input[type="radio"]');
    if (radioInput) {
      radioInput.checked = true;
    }

    // Update selected color name display
    const colorNameEl = this.element.querySelector('.selected-color-name');
    if (colorNameEl) colorNameEl.textContent = this.selectedColor;

    this.dispatchCustomEvent('colorChanged', {
      selectedColor: this.selectedColor,
      selectedColorSku: this.selectedColorSku,
      selectedFeaturedMedia: this.selectedFeaturedMedia,
    });
  }

  deselectAllColorOptions() {
    const allColorOptions = this.element.querySelectorAll('.color-option');
    allColorOptions.forEach((opt) => opt.classList.remove('color-option--selected'));
    const checkedRadio = this.element.querySelector('.color-option__input:checked');
    if (checkedRadio) checkedRadio.checked = false;
    this.selectedColor = null;
    this.selectedColorSku = null;
    this.selectedFeaturedMedia = null;
  }

  /**
   * Handle refill frequency selection change
   * @param {Event} _event - Click event (unused)
   * @param {HTMLElement} option - Selected refill option element
   *
   * FLOW:
   * 1. Extract frequency from option's data-frequency attribute
   * 2. Skip if already selected (no-op)
   * 3. Update selected frequency and reset scent selections
   * 4. Update UI classes and radio button state
   * 5. Trigger state update (pricing, images, etc.)
   * 6. Dispatch custom event for external listeners
   */
  handleRefillSelection(_event, option) {
    const refillFrequency = option.dataset.frequency;
    if (refillFrequency === this.selectedRefillFrequency) return;

    const previousFrequency = this.selectedRefillFrequency;
    this.selectedRefillFrequency = refillFrequency;
    this.resetScentSelections(); // Clear any previous scent choices

    // Update visual selection state
    const allRefillOptions = this.element.querySelectorAll('.refill-option');
    allRefillOptions.forEach((opt) => opt.classList.remove('selected'));
    option.classList.add('selected');

    // Ensure radio button is checked
    const radioInput = option.querySelector('input[type="radio"]');
    if (radioInput) {
      radioInput.checked = true;
    }

    if (this.selectedColor) {
      const firstColorOption = this.element.querySelector('.color-option');
      if (firstColorOption) {
        this.handleColorSelection(null, firstColorOption, true);
      }
    }

    // Hide one-time purchase wrapper and restore link when a subscription plan is selected
    const oneoffWrapper = this.element.querySelector('[data-oneoff-wrapper]');
    if (oneoffWrapper && refillFrequency !== 'oneoff') {
      oneoffWrapper.style.display = 'none';
      const noSubToggle = this.element.querySelector('[data-no-subscription-toggle]');
      if (noSubToggle) noSubToggle.style.display = '';
    }

    // Update all dependent UI elements
    this.updateState();

    // Notify external listeners (e.g., analytics)
    this.dispatchCustomEvent('refillFrequencyChanged', {
      refillFrequency: this.selectedRefillFrequency,
    });
  }

  /**
   * Initialize scent selector to default state
   * Resets selection arrays and updates display
   */
  initializeScentSelector() {
    if (this.selectedRefillFrequency === 'quarterly') {
      this.autoSelectAllScents();
    } else if (this.selectedRefillFrequency === 'trial') {
      this.autoSelectFirstScent();
    } else {
      this.selectedScents = [];
    }
    this.updateScentSelectorDisplay();
    this.updateAddToCartButton();
  }

  /**
   * Auto-select the first available scent for Trial Plan
   */
  autoSelectFirstScent() {
    this.selectedScents = [];
    this.selectedScentSkusOrdered = [];
    const cards = this.element.querySelectorAll('.scent-card');
    for (const card of cards) {
      const addBtn = card.querySelector('.scent-card__add-btn');
      if (addBtn && !addBtn.disabled) {
        this.handleScentAdd(null, card);
        break;
      }
    }
  }

  /**
   * Auto-select all available scents (1 of each) for Discovery Plan
   * Iterates scent cards, skips sold-out/coming-soon, sets qty=1 for each
   */
  autoSelectAllScents() {
    this.selectedScents = [];
    this.selectedScentSkusOrdered = [];

    const scentCards = this.element.querySelectorAll('.scent-card');
    scentCards.forEach((card) => {
      // Skip sold out / coming soon cards
      const addBtn = card.querySelector('.scent-card__add-btn');
      if (addBtn && addBtn.classList.contains('scent-card__add-btn--disabled')) return;

      const productId = card.dataset.productId;
      const variantId = card.dataset.variantId;
      const sellingPlansData = card.dataset.sellingPlans;

      let sellingPlanId = '';
      if (sellingPlansData && this.selectedRefillFrequency !== 'oneoff' && this.selectedRefillFrequency !== 'gift') {
        const sellingPlans = sellingPlansData.split(',');
        if (this.selectedRefillFrequency === 'monthly' && sellingPlans[0]) {
          sellingPlanId = sellingPlans[0];
        } else if (this.selectedRefillFrequency === 'quarterly' && sellingPlans[1]) {
          sellingPlanId = sellingPlans[1];
        } else if (this.selectedRefillFrequency === 'trial' && sellingPlans[2]) {
          sellingPlanId = sellingPlans[2];
        }
      }

      const title = card.querySelector('.scent-card__title')?.textContent?.trim() || '';

      this.selectedScents.push({
        productId,
        variantId,
        quantity: 1,
        sellingPlanId,
        title,
      });

      // Track scent SKU for image matching
      if (card.dataset.scentSku) {
        this.selectedScentSkusOrdered.push(card.dataset.scentSku);
      }

      // Update DOM to reflect selection
      this.setCardQuantity(card, 1);
      card.classList.add('scent-card--selected');
    });
  }

  resetScentSelections() {
    this.selectedScents = [];
    this.selectedAddons = [];
    this.addonsTotalPrice = 0;
    this.selectedScentSku = '';
    this.selectedScentSkusOrdered = [];

    const scentCards = this.element.querySelectorAll('.scent-card');
    scentCards.forEach((card) => {
      card.classList.remove('scent-card--selected');

      this.setCardQuantity(card, 0);

      const addButton = card.querySelector('.scent-card__add-btn');
      const quantityControls = card.querySelector('.scent-card__quantity-controls');

      if (addButton) addButton.style.display = 'block';
      if (quantityControls) quantityControls.style.display = 'none';
    });

    // Quarterly discovery set: auto-select one of each scent
    if (this.selectedRefillFrequency === 'quarterly') {
      this.autoSelectAllScents();
    }

    this.updateScentSelectorDisplay();

    // Trial auto-select runs AFTER updateScentSelectorDisplay so buttons are re-enabled
    if (this.selectedRefillFrequency === 'trial') {
      this.autoSelectFirstScent();
    }

    this.updateAddToCartButton();
    this.updatePricingSummary();

    // Reset gallery image to match current color (not stale scent image)
    this.updateMainImage();
  }

  getMappedRefillFrequency() {
    return this.REFILL_FREQUENCY_MAP[this.selectedRefillFrequency];
  }

  /**
   * Get current pricing option based on selected frequency
   * @returns {Object|null} Current pricing option from prices_map
   */
  getCurrentPricingOption() {
    const pricingData = this.prices_map[this.CUSTOMER_TYPE];
    if (!pricingData) return null;

    // Use mapped frequency for all options including gift
    const mappedFrequency = this.getMappedRefillFrequency();
    return pricingData.find((option) => option.frequency === mappedFrequency);
  }

  formatPrice(priceInPence) {
    return Shopify.formatMoney(priceInPence, this.prices_map.money_format).replace('.00', '');
  }

  getCardQuantity(card) {
    const quantityDisplay = card.querySelector('.scent-card__quantity-display');
    return parseInt(quantityDisplay?.textContent) || 0;
  }

  setCardQuantity(card, quantity) {
    const quantityDisplay = card.querySelector('.scent-card__quantity-display');
    if (quantityDisplay) {
      quantityDisplay.textContent = quantity;
    }
  }

  triggerCardUpdates(card) {
    this.updateSelectedScentsFromDOM();
    this.updateScentCard(card);
    this.updateScentSelectorDisplay();
    this.updateAddToCartButton();
    this.updateState();
  }

  handleScentAdd(_event, card) {
    const currentTotal = this.getTotalSelectedScentsFromDOM();

    // For subscriptions only, limit to required quantity
    // One-time and gift purchases allow unlimited items
    if (this.selectedRefillFrequency !== 'oneoff' && this.selectedRefillFrequency !== 'gift') {
      const requiredQuantity = this.getRequiredScentQuantity();
      if (currentTotal >= requiredQuantity) {
        return;
      }
    }

    const currentQuantity = this.getCardQuantity(card);
    this.setCardQuantity(card, currentQuantity + 1);
    this.selectedScentSkusOrdered.push(card.dataset.scentSku);

    this.triggerCardUpdates(card);

    this.dispatchCustomEvent('scentAdded', {
      productId: card.dataset.productId,
      selectedScents: this.selectedScents,
    });
  }

  handleScentRemove(_event, card) {
    const currentQuantity = this.getCardQuantity(card);

    if (currentQuantity <= 0) {
      return;
    }

    this.setCardQuantity(card, currentQuantity - 1);
    const removeIndex = this.selectedScentSkusOrdered.lastIndexOf(card.dataset.scentSku);
    if (removeIndex !== -1) this.selectedScentSkusOrdered.splice(removeIndex, 1);

    this.triggerCardUpdates(card);

    this.dispatchCustomEvent('scentRemoved', {
      productId: card.dataset.productId,
      selectedScents: this.selectedScents,
    });
  }

  /**
   * Central state update method - triggered whenever selections change
   *
   * ORCHESTRATES:
   * - Stores current state in data attribute (for debugging/external access)
   * - Updates pricing across all options
   * - Updates product images based on frequency
   * - Shows/hides add-ons box for one-time purchases
   * - Updates scent selector counters and button states
   *
   * This is the hub method that keeps all UI in sync with component state.
   */
  updateState() {
    const state = {
      refillFrequency: this.selectedRefillFrequency,
      selectedScents: this.selectedScents,
    };

    // Store state in data attribute for external access/debugging
    this.element.dataset.state = JSON.stringify(state);
    this.mappedRefillFrequency = this.getMappedRefillFrequency();

    // Update all UI elements
    this.updatePricing();
    this.updateDynamicImagery();
    this.updateAddonsVisibility();
    this.updateGiftNotificationVisibility();
    this.updateScentSelectorDisplay();
  }

  updateRefillsPopupText() {
    const refillsPopupText = this.element.querySelector('.refills-popup-text');
    const textHandle = `ongoing_refills_text_once_${this.mappedRefillFrequency}`.toLowerCase().replaceAll(' ', '_');
    if (refillsPopupText) {
      refillsPopupText.innerHTML = this.configData[textHandle];
    }
  }

  updateDynamicImagery() {
    const mediaGallery = document.querySelector('media-gallery.product__gallery');

    // Trial with no scent: show image for the selected color, fallback to trial default
    if (this.selectedRefillFrequency === 'trial' && !this.selectedScentSku) {
      if (this.selectedFeaturedMedia) {
        const colorMediaCell = document.querySelector(`.product__media[data-media-id="${this.selectedFeaturedMedia}"]`);
        if (colorMediaCell && mediaGallery) {
          mediaGallery.setActiveMedia(this.selectedFeaturedMedia, colorMediaCell.classList.contains('product__media--custom-selection'));
          return;
        }
      }
      const trialDefaultImg = document.querySelector('.product__media [alt="full_pack_trial_default"]');
      const trialMediaCell = trialDefaultImg?.closest('.product__media');
      if (trialMediaCell && mediaGallery) {
        mediaGallery.setActiveMedia(trialMediaCell.dataset.mediaId, trialMediaCell.classList.contains('product__media--custom-selection'));
        return;
      }
    }
    const barsImage = document.querySelector('.product-includes__item:nth-child(2) img') || document.querySelector('.product-includes__item:nth-child(1) img');
    const mainImage = document.querySelector('.product__media img');

    if (!barsImage || !mainImage) return;

    if (!this.defaultBarImgSrcset) this.defaultBarImgSrcset = barsImage.srcset;
    if (!this.defaultMainImgSrcset) this.defaultMainImgSrcset = mainImage.srcset;

    const mainImgKey = `main_image_once_per_day_${this.mappedRefillFrequency}_src`.toLowerCase().replaceAll(' ', '_');

    if (this.configData && this.configData[mainImgKey]) {
      mainImage.srcset = this.configData[mainImgKey];
    } else {
      mainImage.srcset = this.defaultMainImgSrcset;
    }

    barsImage.srcset = this.defaultBarImgSrcset;

    // Scroll gallery back to the first slide so the updated image is visible
    if (mediaGallery?.sliderGallery) {
      mediaGallery.sliderGallery.select(1, true);
    }
  }

  /**
   * Update all pricing displays throughout the component
   *
   * UPDATES:
   * - Price displays in each refill option card
   * - "Save" badges with discount amounts
   * - Number of bars text
   * - Recurring price text for subscriptions
   * - Pricing summary block
   *
   * PRICING LOGIC:
   * - Reads from this.prices_map based on CUSTOMER_TYPE
   * - Applies frequency-specific pricing
   * - Shows strikethrough for discounted prices
   * - Updates based on selected quantity (barsQty)
   *
   * Called by updateState() whenever selections change.
   */
  updatePricing() {
    // Get the pricing data (now simplified without washing frequency)
    const currentPricingData = this.prices_map[this.CUSTOMER_TYPE];

    if (!currentPricingData) {
      console.error('No pricing data found');
      return;
    }

    // Update refill option prices for each frequency
    currentPricingData.forEach((option) => {
      let selector;

      if (option.frequency === 'Every Month') {
        selector = '[data-frequency="monthly"]';
      } else if (option.frequency === 'Every 3 Months') {
        selector = '[data-frequency="quarterly"]';
      } else if (option.frequency === 'One off Purchase') {
        selector = '[data-frequency="oneoff"]';
      } else if (option.frequency === 'Gift Purchase') {
        selector = '[data-frequency="gift"]';
      }

      if (selector) {
        const optionElement = this.element.querySelector(selector);
        if (optionElement) {
          // Update main price display
          let priceElements = optionElement.querySelectorAll('.price-display');
          let numberOfBarsElements = optionElement.querySelectorAll('[data-refill-number-of-bars]');
          let numberOfBarsElementsV2 = optionElement.querySelectorAll('[data-refill-number-of-bars-v2]');
          let refillDeliveryPriceElements = optionElement.querySelectorAll('[data-refill-delivery-price]');
          let refillTotalPriceElements = optionElement.querySelectorAll('[data-refill-total-price]');
          let savingsAmountElement = optionElement.querySelector('[data-savings-amount]');
          const barsQty = option.refill?.quantity || option.barsQty;
          if (priceElements && priceElements.length > 0) {
            priceElements.forEach((element) => {
              if (option.price_cents_original && option.price_cents !== option.price_cents_original) {
                // Show discount pricing with strikethrough
                element.innerHTML = `<span class="original-price">${this.formatPrice(option.price_cents_original)}</span> ${this.formatPrice(option.price_cents)}`;
              } else {
                // Show regular pricing
                element.innerHTML = this.formatPrice(option.price_cents);
              }
            });
          }

          if (numberOfBarsElements) {
            numberOfBarsElements.forEach((element) => {
              element.textContent = `${barsQty * 2} x bar${barsQty * 2 > 1 ? 's' : ''}`;
            });
          }

          numberOfBarsElementsV2.forEach((element) => {
            element.textContent = `${barsQty * 2} Bar${barsQty * 2 > 1 ? 's' : ''}`;
          });

          if (refillDeliveryPriceElements) {
            refillDeliveryPriceElements.forEach((element) => {
              element.textContent = this.formatPrice(option.refill.price_cents);
            });
          }

          if (refillTotalPriceElements) {
            refillTotalPriceElements.forEach((element) => {
              element.textContent = this.formatPrice(option.refill.price_cents * barsQty);
            });
          }

          if (savingsAmountElement) {
            const savingsAmount = option.price_cents_original - option.price_cents;
            const savePercentage = ((option.price_cents_original - option.price_cents) / option.price_cents_original) * 100;
            if (this.dataset.discountType === 'percentage') {
              savingsAmountElement.textContent = `Save ${parseInt(savePercentage)}%`;
            } else {
              savingsAmountElement.textContent = `Save ${this.formatPrice(savingsAmount)}`;
            }
            savingsAmountElement.parentElement.classList.toggle('hidden', parseInt(savePercentage) === 0);
          }

          // Update recurring text for subscription options
          let recurringElement = optionElement.querySelector('.recurring-text-js');
          if (recurringElement && option.refill) {
            recurringElement.textContent = `then ${this.formatPrice(option.refill.price_cents * barsQty)}`;
          }
          const topRefillCount =
            document.querySelector('.product-includes__item:nth-child(2) .product-includes__item-title') ||
            document.querySelector('.product-includes__item:nth-child(1) .product-includes__item-title');
          if (optionElement.querySelector('[name="refill_frequency"]:checked')) {
            if (topRefillCount) topRefillCount.textContent = `${barsQty * 2} x Bob bar${barsQty * 2 > 1 ? 's' : ''}`;

            // Update bob bars quantity in the product includes
            const bobBarsQuantityElement = document.querySelectorAll('.js-bob-bars-quantity');
            bobBarsQuantityElement.forEach((element) => {
              element.textContent = `${barsQty * 2} x Bob bar${barsQty * 2 > 1 ? 's' : ''}`;
              const imageContainer = element.closest('.product-include-item')?.querySelector('img');
              if (imageContainer && imageContainer.hasAttribute('data-4-bars-image') && imageContainer.hasAttribute('data-2-bars-image')) {
                imageContainer.src = imageContainer.dataset[`${barsQty * 2}BarsImage`];
              }
            });
          }
        }
      }
    });

    // Update pricing summary block
    this.updatePricingSummary();
  }

  updatePricingSummary() {
    // Get current selected option data
    const selectedOption = this.getCurrentPricingOption();
    if (!selectedOption) return;

    // Update first order price
    const firstOrderPriceEl = this.element.querySelector('.first-order-price');
    //console.log('updating firstOrderPriceEl');
    // Handle gift purchases with multiple recipients
    if (this.selectedRefillFrequency === 'gift' && this.giftRecipientCount > 1) {
      const basePriceCents = selectedOption.price_cents;
      const totalPriceCents = basePriceCents * this.giftRecipientCount;
      const formattedTotal = this.formatPrice(totalPriceCents);
      const formattedBase = this.formatPrice(basePriceCents);

      if (firstOrderPriceEl) {
        firstOrderPriceEl.innerHTML = `Your first order: ${formattedTotal} <span style="font-weight: 400;">(${this.giftRecipientCount}x ${formattedBase})</span>`;
      }
    } else {
      // Normal pricing display for non-gift or single recipient
      if (firstOrderPriceEl && selectedOption.price_cents_original) {
        firstOrderPriceEl.innerHTML = `Your first order: ${this.formatPrice(selectedOption.price_cents)} <span class="original-price ${
          selectedOption.price_cents === selectedOption.price_cents_original ? 'hidden' : ''
        }">${this.formatPrice(selectedOption.price_cents_original)}</span>`;
      } else if (firstOrderPriceEl) {
        //console.log('updating firstOrderPriceEl without original price');
        //console.log(selectedOption.price_cents);
        //console.log(firstOrderPriceEl);
        firstOrderPriceEl.innerHTML = `Your first order: ${this.formatPrice(selectedOption.price_cents)}`;
      }
    }

    // Update refill price text
    const barsQty = selectedOption.refill?.quantity || selectedOption.barsQty;
    const refillPriceEl = this.element.querySelector('.refill-price');
    if (refillPriceEl && selectedOption.refill) {
      const refillFrequencyText = selectedOption.refill.frequency === 'Every Month' ? 'month' : '3 months';
      refillPriceEl.textContent = `+ Refills every ${refillFrequencyText} for ${this.formatPrice(selectedOption.refill.price_cents * barsQty)}`;
    } else if (refillPriceEl && (selectedOption.frequency === 'One off Purchase' || this.selectedRefillFrequency === 'gift')) {
      refillPriceEl.textContent = ''; // No recurring price for one-time or gift
    }

    //console.log('updating addonsTotalPrice');
    //console.log(this.addonsTotalPrice);
    //console.log('selectedOption.price_cents', selectedOption.price_cents);
    // Add add-ons total to first order price if any selected
    if (this.addonsTotalPrice > 0 && firstOrderPriceEl) {
      const currentFirstOrderPrice = selectedOption.price_cents + this.addonsTotalPrice;
      if (selectedOption.price_cents_original) {
        const originalWithAddons = selectedOption.price_cents_original + this.addonsTotalPrice;
        firstOrderPriceEl.innerHTML = `Your first order: ${this.formatPrice(currentFirstOrderPrice)} <span class="original-price">${this.formatPrice(originalWithAddons)}</span>`;
      } else {
        firstOrderPriceEl.innerHTML = `Your first order: ${this.formatPrice(currentFirstOrderPrice)}`;
      }
    }
  }

  dispatchCustomEvent(eventName, detail) {
    //console.log('dispatchCustomEvent', eventName, detail);
    const event = new CustomEvent(`subscription-picker:${eventName}`, {
      detail: detail,
      bubbles: true,
    });
    this.element.dispatchEvent(event);
    //window.dispatchEvent(event);
  }

  /**
   * Get product price for addon calculation
   * @param {string} _productId - Product ID (unused but kept for signature)
   * @param {string} _variantId - Variant ID (unused but kept for signature)
   * @returns {number} Price in pence
   */
  getProductPrice(_productId, _variantId) {
    const currentOption = this.getCurrentPricingOption();
    return currentOption?.per_refill_pack_cost_cents || this.SCENT_ADDON_PRICE_PENCE;
  }

  /**
   * Get the required quantity of scents based on refill frequency
   * @returns {number} Required scent quantity
   */
  getRequiredScentQuantity() {
    const selectedOption = this.getCurrentPricingOption();
    return selectedOption?.refill?.quantity || selectedOption?.barsQty || 1;
  }

  /**
   * Get total number of selected scents from DOM
   * @returns {number} Total selected quantity
   */
  getTotalSelectedScentsFromDOM() {
    const scentCards = this.element.querySelectorAll('.scent-card');
    let total = 0;

    scentCards.forEach((card) => {
      const quantityDisplay = card.querySelector('.scent-card__quantity-display');
      const quantity = parseInt(quantityDisplay?.textContent) || 0;
      total += quantity;
    });

    return total;
  }

  /**
   * Get total number of selected scents
   * @returns {number} Total selected quantity
   */
  getTotalSelectedScents() {
    return this.selectedScents.reduce((total, scent) => total + scent.quantity, 0);
  }

  /**
   * Update selected scents array from current DOM state
   * Reads quantities directly from DOM and rebuilds the selectedScents array
   *
   * IMPORTANT: This is a key method called whenever scents are added/removed.
   * It synchronizes the component's internal state with what's displayed in the UI.
   *
   * LOGIC FLOW:
   * 1. Reset both selectedScents and selectedAddons arrays
   * 2. Iterate through all scent cards, collecting items with quantity > 0
   * 3. For each item, determine the correct selling plan based on frequency
   * 4. For one-time/gift purchases:
   *    - Split items into "included" (up to requiredQuantity) and "addons" (extras)
   *    - Included items go to selectedScents (part of base price)
   *    - Extra items go to selectedAddons (charged at £8 each)
   * 5. For subscriptions:
   *    - All items go to selectedScents (no addon concept)
   *    - Items are grouped by variant ID to consolidate duplicates
   * 6. Recalculate addon pricing and update the pricing summary
   *
   * DATA FLOW:
   * DOM (quantity displays) → allItems array → selectedScents/selectedAddons arrays
   * → calculateAddonsTotal() → updatePricingSummary() → UI updates
   */
  updateSelectedScentsFromDOM() {
    // Reset state before rebuilding from DOM
    this.selectedScents = [];
    this.selectedAddons = [];

    const scentCards = this.element.querySelectorAll('.scent-card');
    const allItems = []; // Temporary array to collect all selected items
    const requiredQuantity = this.getRequiredScentQuantity();

    /**
     * FIRST PASS: Collect all selected items from DOM
     * Creates individual item entries for each unit selected.
     * Example: If "Ordinary Man" has quantity 2, creates 2 separate items.
     * This allows proper splitting between base items and addons later.
     */
    scentCards.forEach((card) => {
      const quantityDisplay = card.querySelector('.scent-card__quantity-display');
      const quantity = parseInt(quantityDisplay?.textContent) || 0;

      if (quantity > 0) {
        const productId = card.dataset.productId;
        const variantId = card.dataset.variantId;
        const sellingPlansData = card.dataset.sellingPlans;
        const title = card.querySelector('.scent-card__title')?.textContent?.trim() || '';

        /**
         * SELLING PLAN SELECTION
         * Recharge selling plans determine the subscription billing cycle.
         * Format in data attribute: "monthly_plan_id,quarterly_plan_id"
         *
         * For subscriptions: Select plan based on chosen frequency
         * For one-time/gift: Leave empty (no subscription attached)
         */
        let sellingPlanId = '';
        if (sellingPlansData && this.selectedRefillFrequency !== 'oneoff' && this.selectedRefillFrequency !== 'gift') {
          const sellingPlans = sellingPlansData.split(',');
          if (this.selectedRefillFrequency === 'monthly' && sellingPlans[0]) {
            sellingPlanId = sellingPlans[0]; // Index 0 = monthly plan
          } else if (this.selectedRefillFrequency === 'quarterly' && sellingPlans[1]) {
            sellingPlanId = sellingPlans[1]; // Index 1 = quarterly plan
          } else if (this.selectedRefillFrequency === 'trial' && sellingPlans[2]) {
            sellingPlanId = sellingPlans[2]; // Index 2 = trial plan
          }
        }

        /**
         * Create individual items for each unit of quantity.
         * This allows us to properly distribute items between
         * selectedScents (included in price) and selectedAddons (extra charge).
         */
        for (let i = 0; i < quantity; i++) {
          allItems.push({
            productId,
            variantId,
            sellingPlanId,
            title,
          });
        }
      }
    });

    // For one-time and gift purchases, split between selectedScents and selectedAddons
    if (this.selectedRefillFrequency === 'oneoff' || this.selectedRefillFrequency === 'gift') {
      // First requiredQuantity items go to selectedScents (included in base price)
      if (allItems.length > 0) {
        const groupedScents = {};
        const addonItems = {};

        // Split items: first requiredQuantity go to selectedScents
        for (let i = 0; i < allItems.length; i++) {
          const item = allItems[i];
          const key = item.variantId;

          if (i < requiredQuantity) {
            // Add to selectedScents
            if (groupedScents[key]) {
              groupedScents[key].quantity += 1;
            } else {
              groupedScents[key] = {
                productId: item.productId,
                variantId: item.variantId,
                quantity: 1,
                sellingPlanId: item.sellingPlanId,
                title: item.title,
              };
            }
          } else {
            // Add to selectedAddons (items beyond requiredQuantity)
            if (addonItems[key]) {
              addonItems[key].quantity += 1;
            } else {
              addonItems[key] = {
                variantId: item.variantId,
                productId: item.productId,
                quantity: 1,
                price: this.getProductPrice(item.productId, item.variantId),
                isSameVariant: false,
              };
            }
          }
        }

        //console.log('addonItems', addonItems);
        // Convert to arrays
        this.selectedScents = Object.values(groupedScents);
        this.selectedAddons = Object.values(addonItems);
      }
    } else {
      // For subscriptions, group by variant
      const groupedItems = {};
      allItems.forEach((item) => {
        const key = item.variantId;
        if (groupedItems[key]) {
          groupedItems[key].quantity += 1;
        } else {
          groupedItems[key] = {
            productId: item.productId,
            variantId: item.variantId,
            quantity: 1,
            sellingPlanId: item.sellingPlanId,
            title: item.title,
          };
        }
      });

      this.selectedScents = Object.values(groupedItems);
    }

    // Recalculate addon pricing
    this.calculateAddonsTotal();
    this.updatePricingSummary();
  }

  /**
   * Update scent card display based on current selection
   * @param {HTMLElement} card - The scent card to update
   */
  updateScentCard(card) {
    const quantity = this.getCardQuantity(card);

    const addBtns = card.querySelectorAll('[data-action="add"]');
    const removeBtn = card.querySelector('[data-action="remove"]');
    const addButton = card.querySelector('.scent-card__add-btn');
    const quantityControls = card.querySelector('.scent-card__quantity-controls');

    // Update remove button icon based on quantity
    if (removeBtn) {
      removeBtn.disabled = quantity === 0;
      //removeBtn.disabled = addButton.classList.contains('scent-card__add-btn--disabled');

      const trashIcon = removeBtn.querySelector('.icon-trash');
      const minusIcon = removeBtn.querySelector('.icon-minus');

      if (trashIcon && minusIcon) {
        if (quantity === 1) {
          // Show trash icon when quantity is 1 (will remove item completely)
          trashIcon.style.display = 'block';
          minusIcon.style.display = 'none';
        } else {
          // Show minus icon when quantity > 1 (will decrease quantity)
          trashIcon.style.display = 'none';
          minusIcon.style.display = 'block';
        }
      }
    }

    const requiredQuantity = this.getRequiredScentQuantity();
    const currentTotal = this.getTotalSelectedScentsFromDOM();
    if (currentTotal > 0) {
      const topRefillCount =
        document.querySelector('.product-includes__item:nth-child(2) .product-includes__item-title') ||
        document.querySelector('.product-includes__item:nth-child(1) .product-includes__item-title');
      if (topRefillCount) topRefillCount.textContent = `${currentTotal * 2} x Bob bar${currentTotal * 2 > 1 ? 's' : ''}`;
      // Update bob bars quantity in the product includes
      const bobBarsQuantityElement = document.querySelectorAll('.js-bob-bars-quantity');
      bobBarsQuantityElement.forEach((element) => {
        element.textContent = `${currentTotal * 2} x Bob bar${currentTotal * 2 > 1 ? 's' : ''}`;
      });
    }

    // For one-time and gift purchases, allow unlimited items
    const canAddMore = this.selectedRefillFrequency === 'oneoff' || this.selectedRefillFrequency === 'gift' ? true : currentTotal < requiredQuantity;

    // Update all add buttons
    addBtns.forEach((addBtn) => {
      if (addBtn) {
        addBtn.disabled = !canAddMore;
        addBtn.disabled = addBtn.classList.contains('scent-card__add-btn--disabled');
      }
    });

    // Toggle between ADD button and quantity controls
    if (quantity > 0) {
      card.classList.add('scent-card--selected');
      if (addButton) addButton.style.display = 'none';
      if (quantityControls) quantityControls.style.display = 'flex';
      this.selectedScentSku = card.dataset.scentSku;
      if (this._initialized) {
        this.dispatchCustomEvent('scentCardUpdated', { scentSku: this.selectedScentSku, selectedScentSkusOrdered: this.selectedScentSkusOrdered });
      }
    } else {
      card.classList.remove('scent-card--selected');
      if (addButton) addButton.style.display = 'block';
      if (quantityControls) quantityControls.style.display = 'none';
    }

    // Disable ADD button if we can't add more
    if (addButton) {
      addButton.disabled = !canAddMore;
    }
    //debugger;
  }

  /**
   * Update all scent selector displays
   */
  updateScentSelectorDisplay() {
    const scentWidget = this.element.querySelector('.scent-selector-widget');
    if (!scentWidget) return;

    const requiredQuantity = this.getRequiredScentQuantity();
    const currentTotal = this.getTotalSelectedScentsFromDOM();

    // Toggle between discovery title and standard title
    const discoveryTitle = scentWidget.querySelector('.scent-title-discovery');
    const oneoffTitle = scentWidget.querySelector('.scent-title-oneoff');
    const subtitleElement = scentWidget.querySelector('.scent-selector-subtitle');
    const selectedCountSpan = scentWidget.querySelector('.selected-count');
    const requiredCountSpan = scentWidget.querySelector('.required-count');

    if (this.selectedRefillFrequency === 'quarterly') {
      // Quarterly discovery set: show discovery title, hide standard
      if (discoveryTitle) discoveryTitle.style.display = '';
      if (oneoffTitle) oneoffTitle.style.display = 'none';
      if (subtitleElement) subtitleElement.style.display = 'none';
    } else {
      // All other modes: show standard title with counter
      if (discoveryTitle) discoveryTitle.style.display = 'none';
      if (oneoffTitle) oneoffTitle.style.display = '';
      if (Number(requiredQuantity) === 1 && this.selectedRefillFrequency === 'oneoff') {
        if (subtitleElement) {
          subtitleElement.style.display = 'block';
        }
      } else {
        if (subtitleElement && this.selectedRefillFrequency !== 'oneoff') {
          subtitleElement.style.display = 'none';
        } else if (subtitleElement && this.selectedRefillFrequency === 'oneoff') {
          subtitleElement.style.display = 'block';
        }
      }
    }

    // Update counter spans if they exist (for subscription mode)
    if (selectedCountSpan) {
      selectedCountSpan.textContent = currentTotal;
    }
    if (requiredCountSpan) {
      requiredCountSpan.textContent = requiredQuantity;
    }

    // Update all cards
    const cards = scentWidget.querySelectorAll('.scent-card');
    cards.forEach((card) => this.updateScentCard(card));
  }

  /**
   * Update add to cart button state based on scent selection
   */
  updateAddToCartButton() {
    const addToCartBtn = this.element.querySelector('[data-add-to-cart-btn]');
    const btnText = this.element.querySelector('[data-btn-text]');
    const currentTotal = this.getTotalSelectedScentsFromDOM();
    if (this.element.dataset.closed === 'true') return;
    if (!addToCartBtn || !btnText) return;

    // Quarterly discovery set: scents are auto-selected, always ready
    if (this.selectedRefillFrequency === 'quarterly') {
      addToCartBtn.disabled = false;
      btnText.textContent = 'Checkout';
      return;
    }

    // For subscriptions, follow exact quantity requirements
    const requiredQuantity = this.getRequiredScentQuantity();
    const remaining = requiredQuantity - currentTotal;

    if (remaining > 0) {
      addToCartBtn.disabled = true;
      btnText.textContent = `Add ${remaining} refill${remaining > 1 ? 's' : ''} to checkout`;
    } else {
      addToCartBtn.disabled = false;
      btnText.textContent = 'Checkout';
    }
  }

  // Public methods for external access
  getState() {
    return {
      refillFrequency: this.selectedRefillFrequency,
      selectedScents: this.selectedScents,
    };
  }

  setRefillFrequency(refillFrequency) {
    const option = this.element.querySelector(`[data-frequency="${refillFrequency}"]`);
    if (option && option.classList.contains('refill-option')) {
      const radioInput = option.querySelector('input[type="radio"]');
      if (radioInput) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  updateAddonsVisibility() {
    // Show add-ons box only for one-time purchase frequency
    //console.log('updateAddonsVisibility', this.selectedRefillFrequency);
    if (this.addonsBox) {
      if (this.selectedRefillFrequency === 'oneoff') {
        this.addonsBox.style.display = '';
        this.setupAddonsEventListeners();
      } else {
        this.addonsBox.style.display = 'none';
        this.resetAddons();
      }
    }
  }

  /**
   * Update gift notification widget visibility
   * Show only when 'gift' refill frequency is selected
   */
  updateGiftNotificationVisibility() {
    if (this.giftNotificationWidget) {
      if (this.selectedRefillFrequency === 'gift') {
        this.giftNotificationWidget.style.display = '';
        // Hide main color picker when gift is selected
        this.element.setAttribute('data-gift-selected', 'true');
      } else {
        this.giftNotificationWidget.style.display = 'none';
        // Show main color picker for non-gift purchases
        this.element.setAttribute('data-gift-selected', 'false');
      }
    }
  }

  setupAddonsEventListeners() {
    // Add event listeners to addon select dropdowns
    const addonSelects = this.addonsBox.querySelectorAll('select[name="addon_product"]');
    addonSelects.forEach((select) => {
      select.removeEventListener('change', this.handleAddonChangeBinding);
      select.addEventListener('change', this.handleAddonChangeBinding);
    });
  }

  handleAddonChange(_event) {
    this.calculateAddonsTotal();
    this.updatePricingSummary();
  }

  calculateAddonsTotal() {
    let total = 0;

    // Calculate traditional add-ons (from add-ons box)
    if (this.addonsBox) {
      const addonSelects = this.addonsBox.querySelectorAll('select[name="addon_product"]');
      addonSelects.forEach((select) => {
        const quantity = parseInt(select.value) || 0;
        const price = parseInt(select.dataset.variantPrice) || 0;
        total += quantity * price;
      });
    }

    // Calculate scent add-ons (for one-time purchases)
    if (this.selectedAddons && this.selectedAddons.length > 0) {
      this.selectedAddons.forEach((addon) => {
        total += addon.quantity * addon.price;
      });
    }

    this.addonsTotalPrice = total;
    return total;
  }

  resetAddons() {
    // Reset all addon selections to 0
    if (this.addonsBox) {
      const addonSelects = this.addonsBox.querySelectorAll('select[name="addon_product"]');
      addonSelects.forEach((select) => {
        select.value = '0';
      });
      this.addonsTotalPrice = 0;
      // Update pricing to remove add-ons from the summary
      this.updatePricingSummary();
    }
  }

  getSelectedAddons() {
    const selectedAddons = [];

    // Get traditional add-ons from the add-ons box
    if (this.addonsBox && this.selectedRefillFrequency === 'oneoff') {
      const addonSelects = this.addonsBox.querySelectorAll('select[name="addon_product"]');
      addonSelects.forEach((select) => {
        const quantity = parseInt(select.value) || 0;
        if (quantity > 0) {
          const isSameVariant = select.dataset.sameVariant === 'true';
          const variantsIds = select.dataset.variantsIds;

          // If we have the new data-variants-ids attribute, parse it and create items for each variant
          if (variantsIds) {
            try {
              const variantIdsArray = JSON.parse(variantsIds);
              variantIdsArray.forEach((variantId) => {
                selectedAddons.push({
                  id: variantId,
                  quantity: quantity,
                  isSameVariant: isSameVariant,
                });
              });
            } catch (e) {
              console.error('Error parsing data-variants-ids:', e);
              // Fallback to single variant if parsing fails
              const variantId = select.dataset.variantId;
              selectedAddons.push({
                id: variantId,
                quantity: quantity,
                isSameVariant: isSameVariant,
              });
            }
          } else {
            // Fallback to old single variant approach
            const variantId = select.dataset.variantId;
            selectedAddons.push({
              id: variantId,
              quantity: quantity,
              isSameVariant: isSameVariant,
            });
          }
        }
      });
    }

    // Add scent add-ons (for one-time purchases with multiple scents)
    if (this.selectedAddons && this.selectedAddons.length > 0) {
      this.selectedAddons.forEach((addon) => {
        selectedAddons.push({
          id: addon.variantId,
          quantity: addon.quantity,
          isSameVariant: addon.isSameVariant,
        });
      });
    }

    return selectedAddons;
  }

  /**
   * Handle Add to Cart button click
   *
   * TWO-STAGE FLOW:
   * 1. If picker is closed (data-closed="true"):
   *    - Expand picker to show scent selector
   *    - Change button text to "Checkout"
   *    - Return early (don't add to cart yet)
   *
   * 2. If picker is open (data-closed="false"):
   *    - Validate scent selection quantity
   *    - Get current pricing option
   *    - Build cart data object
   *    - Dispatch 'addToCartRequested' event
   *    - mps--add-to-cart.js listens for this event and handles Recharge integration
   *
   * VALIDATION:
   * - Subscriptions: Must select exact required quantity
   * - One-time: Must select at least 1 scent (unlimited allowed)
   */
  handleAddToCart({ scroll = false, isTrusted = false } = {}) {
    const addToCartBtn = this.element.querySelector('[data-add-to-cart-btn]');

    // STAGE 1: Expand picker if collapsed
    if (this.element.dataset.closed === 'true') {
      this.element.setAttribute('data-closed', 'false');
      addToCartBtn.querySelector('.btn-text').innerText = 'Checkout';

      // Track widget open in GA4 (only for human clicks)
      if (isTrusted && typeof window.gtag === 'function') {
        window.gtag('event', 'subscription_widget_opened', {
          refill_frequency: this.selectedRefillFrequency,
        });
      }

      if (this.element.querySelector('.color-option--selected')) this.handleColorSelection(null, this.element.querySelector('.color-option--selected'), true);
      this.updateAddToCartButton();
      // Scroll to top of picker when changing steps
      //if (scroll && window.innerWidth < 1024 && document.location.pathname.includes('/products/')) this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // STAGE 2: Validate and prepare cart data
    // Validate scent selection before proceeding
    const currentTotal = this.getTotalSelectedScentsFromDOM();

    // Quarterly discovery set: scents are auto-selected, skip quantity validation
    if (this.selectedRefillFrequency !== 'quarterly') {
      // For subscriptions, require exact quantity
      // For one-time and gift purchases, require at least the base quantity (unlimited allowed)
      const requiredQuantity = this.getRequiredScentQuantity();
      if (
        (currentTotal !== requiredQuantity && !(this.selectedRefillFrequency === 'oneoff' || this.selectedRefillFrequency === 'gift')) ||
        ((this.selectedRefillFrequency === 'oneoff' || this.selectedRefillFrequency === 'gift') && currentTotal < requiredQuantity)
      ) {
        console.warn('Cannot add to cart: Incorrect scent selection quantity');
        return;
      }
    }

    // Get current selected options
    const selectedRefillFrequency = this.selectedRefillFrequency;

    // Validate gift notification data for gift purchases
    if (selectedRefillFrequency === 'gift' && this.giftNotificationWidget) {
      const validation = this.giftNotificationWidget.validateGiftNotification();
      if (!validation.isValid) {
        console.warn('Gift notification validation failed:', validation.errors);
        // Show errors in the form
        this.giftNotificationWidget.showErrors(validation.errors);
        return;
      }
    }

    // Find the current product data based on selections
    const selectedOption = this.getCurrentPricingOption();

    if (!selectedOption) {
      console.error('No product option found for current selection');
      return;
    }

    const addToCartData = {
      refillFrequency: selectedRefillFrequency,
      selectedScents: this.selectedScents,
      selectedColor: this.selectedColor,
      productData: selectedOption,
      selectedAddons: this.getSelectedAddons(),
      pricing: {
        firstOrderPrice: selectedOption.price_cents,
        originalPrice: selectedOption.price_cents_original || selectedOption.price_cents,
        refillPrice: selectedOption.refill ? selectedOption.refill.price_cents : null,
        addonsTotal: this.addonsTotalPrice,
      },
    };

    // Include gift notification data for gift purchases
    if (selectedRefillFrequency === 'gift' && this.giftNotificationWidget) {
      const giftNotificationData = this.giftNotificationWidget.getGiftNotificationData();
      if (giftNotificationData) {
        addToCartData.giftNotification = giftNotificationData;
        addToCartData.giftNotification.klaviyoList = this.giftNotificationWidget.getKlaviyoListName();
      }
    }

    // Dispatch custom event with the selected data
    this.dispatchCustomEvent('addToCartRequested', addToCartData);
  }

  updateTrialRenewalDate() {
    const renewalDateEl = this.element.querySelector('[data-trial-renewal-date]');
    if (!renewalDateEl) return;
    const renewalDate = new Date();
    renewalDate.setDate(renewalDate.getDate() + 14);
    const day = renewalDate.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
    const month = renewalDate.toLocaleDateString('en-GB', { month: 'long' });
    const year = renewalDate.getFullYear();
    renewalDateEl.textContent = `${day}${suffix} ${month} ${year}`;
  }
}

customElements.define('subscription-picker', MonthsSubscriptionPicker);
